import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { 
  HeartIcon, 
  ShoppingBagIcon, 
  SparklesIcon,
  ArrowPathIcon,
  FaceSmileIcon,
  XMarkIcon,
  BuildingStorefrontIcon // Changed from StorefrontIcon
} from '@heroicons/react/24/solid';
import { 
  HeartIcon as HeartOutline, 
  ShoppingBagIcon as ShoppingBagOutline,
  ArrowsRightLeftIcon,
  BuildingStorefrontIcon as BuildingStorefrontOutline // Changed from StorefrontIcon
} from '@heroicons/react/24/outline';

export default function FavoritesPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('products');
  const [favorites, setFavorites] = useState({
    products: [],
    stores: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user) {
      fetchFavorites();
    }
  }, [user]);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/favorites');
      setFavorites(data.data);
    } catch (err) {
      console.error('Error fetching favorites:', err);
      setError('Failed to load your favorites');
    } finally {
      setLoading(false);
    }
  };

  const toggleProductFavorite = async (productId) => {
    try {
      await api.post(`/favorites/products/${productId}`);
      // Update local state instead of refetching
      setFavorites(prev => ({
        ...prev,
        products: prev.products.filter(product => product._id !== productId)
      }));
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  const toggleStoreFavorite = async (storeId) => {
    try {
      await api.post(`/favorites/stores/${storeId}`);
      // Update local state instead of refetching
      setFavorites(prev => ({
        ...prev,
        stores: prev.stores.filter(store => store._id !== storeId)
      }));
    } catch (err) {
      console.error('Error toggling favorite:', err);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md shadow-xl">
          <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <HeartIcon className="w-10 h-10 text-pink-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Oops! Login Required</h2>
          <p className="text-gray-600 mb-6">You need to be logged in to view your favorites</p>
          <Link
            to="/login"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 shadow-lg"
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 pb-16">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
              My Faves 💖
            </h1>
            <div className="flex items-center space-x-2">
              <SparklesIcon className="h-6 w-6 text-yellow-400" />
              <span className="text-sm font-medium text-gray-600">{favorites.products.length + favorites.stores.length} items</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-white p-1 rounded-xl shadow-inner mb-8">
          <button
            onClick={() => setActiveTab('products')}
            className={`flex-1 flex items-center justify-center py-3 px-4 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'products'
                ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ShoppingBagIcon className="w-5 h-5 mr-2" />
            Products ({favorites.products.length})
          </button>
          <button
            onClick={() => setActiveTab('stores')}
            className={`flex-1 flex items-center justify-center py-3 px-4 rounded-xl text-sm font-medium transition-all ${
              activeTab === 'stores'
                ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <BuildingStorefrontIcon className="w-5 h-5 mr-2" />
            Stores ({favorites.stores.length})
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <ArrowPathIcon className="h-12 w-12 text-pink-500 animate-spin mb-4" />
            <p className="text-gray-600">Loading your faves...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XMarkIcon className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Oops!</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchFavorites}
              className="px-4 py-2 bg-pink-500 text-white rounded-full text-sm font-medium hover:bg-pink-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : (
          <>
            {/* Products Tab */}
            {activeTab === 'products' && (
              <div>
                {favorites.products.length === 0 ? (
                  <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
                    <div className="w-24 h-24 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <ShoppingBagOutline className="w-12 h-12 text-pink-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No favorite products yet</h3>
                    <p className="text-gray-600 mb-6">Start adding products to your faves by clicking the heart icon!</p>
                    <Link
                      to="/products"
                      className="inline-flex items-center px-5 py-2.5 bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
                    >
                      <SparklesIcon className="w-4 h-4 mr-2" />
                      Explore Products
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                    {favorites.products.map((product) => (
                      <div key={product._id} className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group">
                        <div className="relative">
                          <Link to={`/product/${product._id}`}>
                            <img
                              src={product.design?.image || product.design?.thumbnail || '/placeholder-product.jpg'}
                              alt={product.name}
                              className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </Link>
                          <button
                            onClick={() => toggleProductFavorite(product._id)}
                            className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors"
                          >
                            <HeartIcon className="h-5 w-5 text-pink-500" />
                          </button>
                          {product.store?.logo && (
                            <div className="absolute bottom-3 left-3">
                              <img
                                src={product.store.logo}
                                alt={product.store.name}
                                className="w-8 h-8 rounded-full border-2 border-white shadow-md"
                              />
                            </div>
                          )}
                        </div>
                        <div className="p-4">
                          <Link to={`/product/${product._id}`}>
                            <h3 className="font-bold text-gray-900 truncate group-hover:text-pink-600 transition-colors">{product.name}</h3>
                          </Link>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{product.description}</p>
                          <div className="flex items-center justify-between mt-3">
                            <span className="font-bold text-pink-600">
                              ${product.variants && product.variants.length > 0 
                                ? product.variants[0].finalPrice 
                                : 'N/A'}
                            </span>
                            <button className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full hover:bg-gray-200 transition-colors">
                              <ArrowsRightLeftIcon className="w-3 h-3 inline mr-1" />
                              View
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Stores Tab */}
            {activeTab === 'stores' && (
              <div>
                {favorites.stores.length === 0 ? (
                  <div className="bg-white rounded-2xl p-8 text-center shadow-lg">
                    <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <BuildingStorefrontIcon className="w-12 h-12 text-purple-400" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No favorite stores yet</h3>
                    <p className="text-gray-600 mb-6">Start adding stores to your faves by clicking the heart icon!</p>
                    <Link
                      to="/stores"
                      className="inline-flex items-center px-5 py-2.5 bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
                    >
                      <SparklesIcon className="w-4 h-4 mr-2" />
                      Explore Stores
                    </Link>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {favorites.stores.map((store) => (
                      <div key={store._id} className="bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 group">
                        <div className="relative h-40 overflow-hidden">
                          <Link to={`/store/${store.slug}`}>
                            <img
                              src={store.banner?.url || store.logo?.url || '/placeholder-store.jpg'}
                              alt={store.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </Link>
                          <button
                            onClick={() => toggleStoreFavorite(store._id)}
                            className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md hover:bg-pink-50 transition-colors"
                          >
                            <HeartIcon className="h-5 w-5 text-pink-500" />
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                            <Link to={`/store/${store.slug}`}>
                              <h3 className="font-bold text-white truncate">{store.name}</h3>
                            </Link>
                          </div>
                        </div>
                        <div className="p-4">
                          <div className="flex items-center mb-3">
                            <img
                              src={store.logo?.url || '/placeholder-avatar.jpg'}
                              alt={store.name}
                              className="w-10 h-10 rounded-full object-cover mr-3 border-2 border-white shadow-md"
                            />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{store.owner?.firstName} {store.owner?.lastName}</p>
                              <p className="text-xs text-gray-500">{store.category}</p>
                            </div>
                          </div>
                          {store.description && (
                            <p className="text-sm text-gray-600 line-clamp-2 mb-3">{store.description}</p>
                          )}
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>{store.stats?.totalProducts || 0} products</span>
                            <span>{store.stats?.totalSales || 0} sales</span>
                            <div className="flex items-center">
                              <FaceSmileIcon className="w-4 h-4 text-yellow-400 mr-1" />
                              {store.stats?.averageRating ? store.stats.averageRating.toFixed(1) : 'New'}
                            </div>
                          </div>
                          <Link
                            to={`/store/${store.slug}`}
                            className="block w-full mt-4 py-2 text-center bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors"
                          >
                            Visit Store
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Floating Action Button */}
      {!loading && (favorites.products.length > 0 || favorites.stores.length > 0) && (
        <div className="fixed bottom-6 right-6 z-10">
          <button
            onClick={fetchFavorites}
            className="p-3 bg-white rounded-full shadow-lg hover:shadow-xl transition-shadow"
          >
            <ArrowPathIcon className="w-6 h-6 text-pink-500" />
          </button>
        </div>
      )}
    </div>
  );
}